import { Link } from 'react-router-dom';
import { useProjects } from '@/api/queries';
import { useAuthStore } from '@/lib/auth-store';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/StatusChip';
import { formatRelative } from '@/lib/time';

/**
 * The project list.
 *
 * The list always renders, even for a client holding one product: the brand
 * mark in the rail points here, and a link that silently bounces past its own
 * destination reads as a dead control.
 *
 * The empty state is not a decoration here. This dashboard is genuinely empty
 * until an admin assigns a project, so the first thing a brand-new client sees
 * is this screen, and it has to explain a situation they did not cause and
 * cannot fix.
 */

export function ProjectsPage() {
  const projects = useProjects();
  const user = useAuthStore((s) => s.user);

  if (projects.isLoading) return <ListSkeleton />;

  if (projects.isError) {
    const err = projects.error instanceof ApiError ? projects.error : null;
    return (
      <Centered>
        <div className="rounded-lg bg-bone p-7">
          <h1 className="text-[1.375rem] font-bold tracking-[-0.025em] text-graphite-900">
            We could not load your chats
          </h1>
          <p className="mt-1.5 text-graphite-500">{err?.message ?? 'Try again in a moment.'}</p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <Button variant="primary" onClick={() => void projects.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Centered>
    );
  }

  const list = projects.data ?? [];

  if (list.length === 0) return <EmptyState name={user?.display_name} />;

  return (
    <div className="scroll-thin h-full overflow-y-auto px-[34px] pb-[46px] pt-[34px] max-[900px]:px-[18px]">
      <h1 className="text-[2.375rem] font-bold leading-[1.06] tracking-[-0.03em] text-graphite-900 max-[900px]:text-[1.6875rem]">
        Your chats
      </h1>
      <p className="mt-2 text-[0.90625rem] text-graphite-500">
        Open a product to see its thread and ask for a change.
      </p>

      <ul className="mt-6">
        {list.map((project) => (
          <li key={project.id}>
            <Link
              to={`/projects/${project.id}`}
              className="mb-3 grid w-full grid-cols-[1fr_auto] items-center gap-x-[18px] gap-y-2 rounded-lg bg-bone px-[22px] py-5 transition-colors hover:bg-mint"
            >
              <span className="flex flex-wrap items-center gap-3">
                <h2 className="text-[1.3125rem] font-bold tracking-[-0.025em] text-graphite-900">{project.name}</h2>
                <StatusChip status={project.status} awaitingAnswer={project.awaiting_answer ?? false} />
              </span>

              <span className="col-span-2 flex flex-wrap gap-x-[18px] gap-y-1 font-mono text-[0.71875rem] text-graphite-500">
                <span>last activity {formatRelative(project.last_activity_at)}</span>
                {project.preview?.state === 'ready' ? <span>preview up to date</span> : null}
                {project.preview?.state === 'building' ? <span>preview updating</span> : null}
                {project.done_available ? <span className="text-signal">ready to hand over</span> : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The empty state.
 *
 * An empty screen is an invitation to act - except when the person reading it
 * cannot act, which is exactly this case. So it does the next most useful
 * thing: says plainly that nothing is wrong, says who is doing the thing that
 * is missing, and gives the one action that is actually available.
 */
function EmptyState({ name }: { name?: string }) {
  return (
    <Centered>
      <div className="rounded-lg bg-bone p-7 text-left">
        <p className="eyebrow">Nothing assigned yet</p>
        <h1 className="mt-2.5 text-[1.375rem] font-bold tracking-[-0.025em] text-graphite-900">
          {name ? `You are all set, ${name.split(' ')[0]}` : 'You are all set'}
        </h1>
        <p className="mt-2 max-w-[34rem] text-graphite-500">
          Nothing is wrong. Your account manager connects each product to your account once it is ready for you — from
          then on it appears here, and you can ask for changes in plain English and watch them happen.
        </p>
        <div className="mt-5">
          <a
            href="mailto:support@example.com"
            className="inline-flex h-11 items-center rounded-full bg-graphite-900 px-[22px] text-[0.90625rem] font-semibold text-white hover:bg-graphite-700"
          >
            Get in touch
          </a>
        </div>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center px-[34px] py-10 max-[900px]:px-[18px]">
      <div className="w-full max-w-[36rem]">{children}</div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="px-[34px] pt-[34px] max-[900px]:px-[18px]">
      <div className="skeleton h-9 w-56 animate-sweep" />
      <div className="mt-7 flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-lg bg-bone px-[22px] py-5">
            <div className="skeleton h-5 w-[15rem] animate-sweep" />
            <div className="skeleton mt-3 h-3 w-[13rem] animate-sweep" />
          </div>
        ))}
      </div>
      <p className="eyebrow mt-6">Loading your chats</p>
      <span className="sr-only">Loading your chats</span>
    </div>
  );
}
